<template>
  <div class="page-preview-modal-wrapper">
    <div class="header">
      <div class="modal-close" @click="onClose"></div>
      <div class="left-content">
        <div class="size-input-wrapper">
          <label>W</label>
          <input
            class="width-input"
            type="number"
            @keyup="onSizeKeyUp($event, 'width')"
            @blur="setPreviewSize($event, 'width')"
            :value="width"
          />
        </div>
        <div class="size-input-wrapper">
          <label>H</label>
          <input
            class="height-input"
            type="number"
            @keyup="onSizeKeyUp($event, 'height')"
            @blur="setPreviewSize($event, 'height')"
            :value="height"
          />
        </div>
        <div class="zoom-wrapper">
          <b-icon
            icon="plus"
            class="fs-3 zoom-icon"
            @click="changeScale(+10)"
          ></b-icon>
          <input
            class="zoom-input"
            type="number"
            @blur="onScaleBlur"
            :value="scale"
            :max="maxScale"
            :min="minScale"
          />
          <b-icon
            icon="dash"
            class="fs-3 zoom-icon"
            @click="changeScale(-10)"
          ></b-icon>
        </div>
      </div>

      <div class="screen-selector">
        <div
          v-for="screen in screens"
          :key="screen.name"
          @click="onSelectScreen(screen)"
        >
          <img
            :class="{ active: screen.name == selectedScreen }"
            class="screen-icon"
            :src="
              getIcons(
                screen.name == selectedScreen ? screen.activeIcon : screen.icon
              )
            "
          />
        </div>
      </div>
    </div>

    <div class="content" ref="previewArea">
      <div class="preview-container">
        <div class="preview-wrapper" :style="getPreviewWrapperStyle">
          <div
            v-if="getSelectedScreenInfo?.isCameraNotch"
            class="camera-notch"
            :style="{
              width: getSelectedScreenInfo?.notchWidth,
            }"
          ></div>
          <iframe
            v-if="url"
            class="preview-iframe"
            :src="url"
            ref="iframe"
            @load="onIframeLoad"
          ></iframe>
          <template v-else>
            <iframe ref="previewFrame" class="preview-iframe"></iframe>
          </template>
        </div>
        <div
          class="resize-handle right"
          @mousedown="onResizeStart('horizontal', $event)"
        >
          <img
            :style="getRightResizeHandlerImageStyles"
            :src="getIcons('drag-handle.svg')"
          />
        </div>
        <div
          class="resize-handle bottom"
          :style="getBottomResizeHandler"
          @mousedown="onResizeStart('vertical', $event)"
        >
          <img :src="getIcons('drag-handle.svg')" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import FormBuilder from '@/components/FormBuilder/FormBuilderV3.vue';
export default {
  data() {
    return {
      screens: [
        {
          label: 'Mobile',
          name: 'mobile',
          width: 375,
          height: 667,
          icon: 'mobile-screen.svg',
          activeIcon: 'mobile-screen-active.svg',
          borderRadius: '40px',
          isCameraNotch: true,
          notchWidth: '100px',
        },
        {
          label: 'Tablet',
          name: 'tablet',
          width: 768,
          height: 1024,
          icon: 'tablet-screen.svg',
          activeIcon: 'tablet-screen-active.svg',
          borderRadius: '20px',
          isCameraNotch: true,
          notchWidth: '150px',
        },
        {
          label: 'Laptop',
          name: 'laptop',
          width: 1440,
          height: 900,
          icon: 'laptop-screen.svg',
          activeIcon: 'laptop-screen-active.svg',
          borderRadius: '8px',
          isCameraNotch: false,
        },
        {
          label: 'Desktop',
          name: 'desktop',
          width: 1920,
          height: 1080,
          icon: 'dekstop-screen.svg',
          activeIcon: 'dekstop-screen-active.svg',
          borderRadius: '8px',
          isCameraNotch: false,
        },
      ],

      selectedScreen: 'laptop',

      width: 0,
      height: 0,

      scale: 100,

      maxScale: 200,
      minScale: 50,

      minWidth: 100,
      minHeight: 100,
    };
  },

  props: {
    url: {
      type: String,
      default: '',
    },
    extraStyles: {
      type: String,
      default: '',
    },
    formBuilderProps: {
      type: Object,
      required: false,
    },
  },
  created() {
    this.setSelectedScreenSize();
    if (!this.url) this.$emit('onPreviewLoad');
  },
  mounted() {
    if (!this.url) this.loadIframe();
  },
  watch: {
    getCurrentScreen(newVal) {
      if (this.url) return;
      const iframe = this.$refs.previewFrame;
      iframe.contentWindow.postMessage(
        { type: 'update_screen', currentScreen: newVal },
        '*'
      );
    },
  },
  methods: {
    injectCss(href, targetDocument) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      targetDocument.head.appendChild(link);
    },
    loadIframe() {
      const iframe = this.$refs.previewFrame;

      // Inject Vue
      const scriptVue = document.createElement('script');
      scriptVue.src = 'https://cdn.jsdelivr.net/npm/vue@2/dist/vue.min.js';
      iframe.contentWindow.document.head.appendChild(scriptVue);

      // Inject BootstrapVue
      const scriptBootstrapVue = document.createElement('script');
      scriptBootstrapVue.src =
        'https://cdn.jsdelivr.net/npm/bootstrap-vue@2.23.1/dist/bootstrap-vue.min.js';
      iframe.contentWindow.document.head.appendChild(scriptBootstrapVue);

      // Inject CSS
      this.injectCss(
        'https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css',
        iframe.contentWindow.document
      );
      this.injectCss(
        'https://cdn.jsdelivr.net/npm/bootstrap-vue@2.23.1/dist/bootstrap-vue.min.css',
        iframe.contentWindow.document
      );

      const styles = Array.from(
        document.querySelectorAll('link[rel="stylesheet"], style')
      );
      styles.forEach((styleNode) => {
        const newNode = styleNode.cloneNode(true);
        iframe.contentWindow.document.head.appendChild(newNode);
      });

      // Wait for BootstrapVue to load
      scriptBootstrapVue.onload = () => {
        const container = document.createElement('div');
        container.id = 'container';
        iframe.contentWindow.document.body.appendChild(container);
        const parentProps = JSON.parse(
          JSON.stringify({
            ...this.formBuilderProps,
            currentScreen: this.getCurrentScreen,
          })
        );

        const originalWarn = window.console.error;
        window.console.error = function (...args) {
          // Check if the warning is the specific prop mutation error from iframe
          const warningMessage = args.join(' ');
          if (
            warningMessage.includes('$listeners is readonly') ||
            warningMessage.includes('$attrs is readonly') ||
            (warningMessage.includes('Avoid mutating a prop directly') &&
              warningMessage.includes('currentScreen'))
          ) {
            // Suppress this specific warning
            return;
          }
          // Allow other warnings to pass through
          originalWarn.apply(console, args);
        };
        const iframeVue = new iframe.contentWindow.Vue({
          el: container,
          data() {
            return {
              reactiveProps: {},
            };
          },
          created() {
            this.reactiveProps = JSON.parse(JSON.stringify(parentProps));
          },
          mounted() {
            iframe.contentWindow.addEventListener(
              'message',
              this.handleMessage
            );
          },
          beforeDestroy() {
            iframe.contentWindow.removeEventListener(
              'message',
              this.handleMessage
            );
            window.console.error = originalWarn;
          },

          methods: {
            handleMessage(event) {
              if (event.data && event.data.currentScreen !== undefined) {
                this.reactiveProps.currentScreen = event.data.currentScreen;
              }
            },
          },
          render(h) {
            return h(FormBuilder, {
              props: this.reactiveProps,
            });
          },
        });
      };
    },
    onClose() {
      this.$emit('onClose');
    },
    getIcons(iconName) {
      return require(`@/assets/img/page-builder-icons/${iconName}`);
    },
    onSelectScreen(screen) {
      this.selectedScreen = screen.name;
      this.scale = 100;
      this.setSelectedScreenSize();
    },
    changeScale(zoom) {
      if (
        this.scale + zoom <= this.maxScale &&
        this.scale + zoom >= this.minScale
      ) {
        this.scale += zoom;
        this.adjustPreviewSize();
      }
    },
    onScaleBlur(event) {
      if (this.scale == event.target.value) return;
      let scale = parseInt(event.target.value);
      if (isNaN(scale)) {
        scale = 0;
      }
      this.scale = this.getRangeValue(scale, this.minScale, this.maxScale);
      event.target.value = this.scale;

      this.adjustPreviewSize();
    },
    adjustPreviewSize() {
      if (this.selectedScreen) return;
      const { width, height } = this.getPreviewWindowSize();
      this.width = this.width < parseInt(width) ? this.width : parseInt(width);
      this.height =
        this.height < parseInt(height) ? this.height : parseInt(height);
    },
    setSelectedScreenSize() {
      const currentScreen = this.screens.find(
        (screen) => screen.name == this.selectedScreen
      );
      this.width = currentScreen.width;
      this.height = currentScreen.height;
    },
    // trigger while change width or height with arrow up or down
    onSizeKeyUp(event, type) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        this.setPreviewSize(event, type);
        this.selectedScreen = '';
      }
    },
    // trigger while blur from height or width input
    setPreviewSize(event, type) {
      let size = parseInt(event.target.value);
      if (isNaN(size)) {
        size = 0;
      }
      const windowSize = this.getPreviewWindowSize();
      const windowMaxSize = windowSize[type];
      const minSize = type == 'height' ? this.minHeight : this.minWidth;
      this[type] = this.getRangeValue(size, minSize, windowMaxSize);
      size <= parseInt(windowMaxSize) ? size : parseInt(windowMaxSize);

      event.target.value = this[type];
      this.selectedScreen = '';
    },
    // trigger while resize with handler
    updateSize(newWidth, newHeight) {
      const { width, height } = this.getPreviewWindowSize();
      if (newWidth != this.width) {
        this.width = this.getRangeValue(newWidth, this.minWidth, width);
      }
      if (newHeight != this.height) {
        this.height = this.getRangeValue(newHeight, this.minHeight, height);
      }
      this.$emit('onSizeChange', this.width, this.height);
    },

    getRangeValue(newValue, minValue, maxValue) {
      return Math.max(
        parseInt(minValue),
        Math.min(parseInt(newValue), parseInt(maxValue))
      );
    },
    getPreviewWindowSize() {
      const headerheight = 100;
      const windowWidth = window.innerWidth / this.zoomScale;
      const windowHeight = (window.innerHeight - headerheight) / this.zoomScale;

      return {
        width: windowWidth - 40 / this.zoomScale,
        height: windowHeight - 25 / this.zoomScale,
      };
    },

    onResizeStart(direction, event) {
      event.preventDefault();
      const iframe = this.$refs.iframe || this.$refs.previewFrame;
      if (iframe) {
        iframe.style.pointerEvents = 'none';
      }
      const startX = event.clientX;
      const startY = event.clientY;
      const startWidth = this.width * this.zoomScale;
      const startHeight = this.height * this.zoomScale;
      const resize = (e) => {
        if (this.selectedScreen) {
          this.selectedScreen = '';
        }

        if (direction === 'horizontal') {
          const newWidth =
            startWidth +
            ((e.clientX - startX) / this.zoomScale) * (2 * this.zoomScale); //  (2 * this.zoomScale) to make drag speed 2x as it expand from right and left both side
          this.updateSize(newWidth / this.zoomScale, this.height);
        } else if (direction === 'vertical') {
          const newHeight = startHeight + e.clientY - startY;
          this.updateSize(this.width, newHeight / this.zoomScale);
        }
      };

      const stopResize = () => {
        window.removeEventListener('mousemove', resize);
        window.removeEventListener('mouseup', stopResize);
        const iframe = this.$refs.iframe || this.$refs.previewFrame;
        if (iframe) {
          iframe.style.pointerEvents = 'auto';
        }
      };

      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResize);
    },
    onIframeLoad() {
      this.$emit('onPreviewLoad');
    },
  },
  computed: {
    getSelectedScreenInfo() {
      return this.screens.find((screen) => screen.name == this.selectedScreen);
    },
    getPreviewWrapperStyle() {
      return {
        width: `${this.width}px`,
        height: `${this.height}px`,
        transform: `scale(${this.zoomScale}) translateX(calc(-50%))`,
        transformOrigin: 'top left',
        borderRadius: this.getSelectedScreenInfo?.borderRadius || '8px',
      };
    },
    zoomScale() {
      return (this.scale - 2) / 100;
    },
    getResizeHandlerSize() {
      return `${4 / this.zoomScale}px`;
    },

    getRightResizeHandlerImageStyles() {
      const halfHeightOfHandler = 42;
      const { width, height } = this.getPreviewWindowSize();
      return {
        transform: `translate(calc(-50% + (${
          this.width * this.zoomScale
        }px / 2) + ${10 + 2 * this.zoomScale}px ), calc(0%))`,
        top: `${
          ((this.height < height ? this.height : height) * this.zoomScale) / 2 -
          halfHeightOfHandler
        }px`,
      };
    },
    getBottomResizeHandler() {
      return {
        height: this.getResizeHandlerSize,
        top: `${this.height * this.zoomScale - 30}px`,
      };
    },

    getCurrentScreen() {
      if (this.width < 768) return 'mobile';
      else if (this.width < 1024) return 'tablet';
      else if (this.width < 1440) return 'laptop';
      else return 'desktop';
    },
  },
  components: {
    FormBuilder,
  },
};
</script>

<style scoped>
.page-preview-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background-color: #ffffff;
}
.page-preview-modal-wrapper .header {
  border-bottom: 1px solid #c8c8c8;
  height: 100px;
}
.page-preview-modal-wrapper .preview-iframe {
  height: 100%;
  width: 100%;
}

/* header style start */
.page-preview-modal-wrapper .header {
  padding-inline: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-preview-modal-wrapper .left-content {
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: center;
}
.page-preview-modal-wrapper .screen-selector {
  display: flex;
  flex-direction: row;
  gap: 24px;
  justify-content: center;
  align-items: center;
}

.page-preview-modal-wrapper .screen-icon.active {
  filter: invert(0%) sepia(100%) saturate(0%) hue-rotate(179deg) brightness(93%)
    contrast(107%);
}

.page-preview-modal-wrapper .zoom-wrapper {
  display: flex;
  align-items: center;
  flex-direction: row;
  width: 100px;
}

.page-preview-modal-wrapper .zoom-wrapper .zoom-icon {
  cursor: pointer;
}
.page-preview-modal-wrapper .zoom-wrapper .zoom-input {
  outline: none;
  width: 100%;
  border: 0.5px solid #adadad;
  border-radius: 5px;
  padding-left: 9px;
  cursor: text;
}

.page-preview-modal-wrapper
  .zoom-wrapper
  .zoom-input::-webkit-inner-spin-button,
.page-preview-modal-wrapper
  .zoom-wrapper
  .zoom-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
/* header style end */

/* content style start */
.page-preview-modal-wrapper .content {
  /* overflow: hidden; */
  height: calc(100% - 100px);
}
.page-preview-modal-wrapper .preview-container {
  position: relative;
  top: 0;
  left: 0;
}
.page-preview-modal-wrapper .preview-wrapper {
  position: relative;
  left: 50%;
  outline: 4px solid #737373;
  margin-top: 8px;
  overflow: hidden;
}
.page-preview-modal-wrapper .camera-notch {
  border: 2.5px solid #737373;
  position: absolute;
  z-index: 10;
  top: 16px;
  width: 100px;
  border-radius: 8px;
  left: 50%;
  transform: translateX(-50%);
}
.page-preview-modal-wrapper .resize-handle {
  transition: opacity 0.3s ease;
}
.page-preview-modal-wrapper .resize-handle.right {
  top: 0;
  height: 100%;
  right: -12px;
  cursor: ew-resize;
}
.page-preview-modal-wrapper .resize-handle.right img {
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
}
.page-preview-modal-wrapper .resize-handle.bottom img {
  transform: rotate(90deg);
}
.page-preview-modal-wrapper .resize-handle.bottom {
  bottom: 28px;
  left: 0;
  width: 100%;
  cursor: ns-resize;
  text-align: center;
  position: absolute;
}
.page-preview-modal-wrapper .size-input-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding-inline: 10px;
  width: 100px;
  border: 0.5px solid #adadad;
  border-radius: 5px;
}
.page-preview-modal-wrapper .size-input-wrapper label {
  line-height: normal;
}
.page-preview-modal-wrapper .size-input-wrapper input {
  width: 100%;
  outline: none;
  cursor: text;
}
/* content style end */
</style>
